import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHandlerFn,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthFlowService } from '../data-access/auth-flow.service';
import { ErrorHandlingService, ErrorInfo } from '@angular-monorepo/data-access';

const OAUTH_ENDPOINTS = [
  '/oauth2/token',
  '/oauth2/authorize',
  '/oauth2/revoke',
  '/oauth2/logout',
  '/.well-known/openid-configuration',
  '/jwks.json',
];

const PUBLIC_ENDPOINTS = [
  '/api/tmetric/invitations/accept-invitation',
];

type HttpForwardFn = (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>;

function isOAuthRequest(url: string): boolean {
  return OAUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function isPublicRequest(url: string): boolean {
  return PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function addTokenHeader<T>(request: HttpRequest<T>, token: string, prefix: string): HttpRequest<T> {
  return request.clone({
    headers: request.headers.set('Authorization', `${prefix} ${token}`),
  });
}

function forwardWithAuth(
  request: HttpRequest<unknown>,
  forward: HttpForwardFn,
  authFlowService: AuthFlowService,
  errorHandlingService: ErrorHandlingService,
): Observable<HttpEvent<unknown>> {
  if (isOAuthRequest(request.url) || isPublicRequest(request.url)) {
    return forward(request);
  }

  const token = authFlowService.getCurrentToken();
  const config = authFlowService.getTokenConfig();
  const authRequest = token ? addTokenHeader(request, token, config.tokenPrefix) : request;

  return forward(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      const errorInfo = errorHandlingService.handleError(error);

      if (errorInfo.code !== 'UNAUTHORIZED' || !token || !errorInfo.requiresAuth) {
        return throwError(() => errorInfo);
      }

      return authFlowService.handleTokenRefresh().pipe(
        switchMap((newToken) => {
          const refreshConfig = authFlowService.getTokenConfig();
          const newRequest = addTokenHeader(request, newToken, refreshConfig.tokenPrefix);
          return forward(newRequest);
        }),
        catchError((refreshError) => {
          const refreshErrorInfo = isErrorInfo(refreshError)
            ? refreshError
            : errorHandlingService.handleError(refreshError);
          return throwError(() => refreshErrorInfo);
        }),
      );
    }),
  );
}

function isErrorInfo(value: unknown): value is ErrorInfo {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'code' in value && 'message' in value;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authFlowService = inject(AuthFlowService);
  private readonly errorHandlingService = inject(ErrorHandlingService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return forwardWithAuth(
      request,
      (requestToForward) => next.handle(requestToForward),
      this.authFlowService,
      this.errorHandlingService,
    );
  }
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authFlowService = inject(AuthFlowService);
  const errorHandlingService = inject(ErrorHandlingService);

  return forwardWithAuth(
    req,
    (requestToForward) => next(requestToForward),
    authFlowService,
    errorHandlingService,
  );
};
